import { generateAnnotationLabel } from 'features/kestrelViewer/hooks/useAnnotationCounters'
import { recordAnnotationCreated } from 'features/kestrelViewer/metrics'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { Vector2 } from 'three'
import {
  KestrelInspectionType,
  KVAerospaceAnnotation,
  KVAnnotation,
  KVG1N6Annotation,
  KVNavyAnnotation,
  KVPolygonGeometry,
  RolodexEntryPatchJson,
  WorldPosition,
} from 'types'

import { KVMessageDef } from './index'

export function getNewPolygonAnnotationMessage() {
  return {
    type: 'newSurfaceArea',
    requiredData: ['surfaceAreaId'],

    callback: (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const annotation = new KVAnnotation(json.surfaceAreaId as string)
      annotation.label = generateAnnotationLabel(
        json.surfaceAreaLabel as string,
        kv.nextAnnotationNumber
      )
      annotation.visible = json.surfaceAreaVisible as boolean
      annotation.severity = (json.surfaceAreaSeverity as string) ?? '3'
      annotation.userName = kv.user?.name ?? ''
      annotation.userEmail = kv.user?.email ?? ''

      // Add the segmentation flag
      annotation.isSegmentationSurfaceArea =
        (json.isSegmentationSurfaceArea as boolean) ?? false

      // Add area and perimeter as numbers
      annotation.area = json.area ? parseFloat(json.area as string) : undefined
      annotation.perimeter = json.perimeter
        ? parseFloat(json.perimeter as string)
        : undefined

      // Add bounding box dimensions
      annotation.boundingBoxLength = json.boundingBoxLength
        ? parseFloat(json.boundingBoxLength as string)
        : undefined
      annotation.boundingBoxWidth = json.boundingBoxWidth
        ? parseFloat(json.boundingBoxWidth as string)
        : undefined

      // Construct point geometry
      const polygonGeometry = new KVPolygonGeometry(
        json.surfaceAreaPoints as WorldPosition[],
        json.normal as WorldPosition
      )
      annotation.geometry = polygonGeometry

      // Centered normal/position for zooming and photo selection
      annotation.centeredNormal = json.normal as WorldPosition
      annotation.centeredPosition = json.centeredPosition as WorldPosition

      const inspectionType = kv.kestrelModel?.data.inspection_type

      // Add Navy annotation if Navy inspection type
      if (inspectionType === KestrelInspectionType.NAVY) {
        annotation.navyAnnotation = new KVNavyAnnotation()
      }
      // Add G1N6 annotation if G1N6 inspection type
      if (inspectionType === KestrelInspectionType.G1N6) {
        annotation.g1n6Annotation = new KVG1N6Annotation()
      }
      // Add Aerospace annotation if Aerospace inspection type
      if (inspectionType === KestrelInspectionType.AEROSPACE) {
        annotation.aerospaceAnnotation = new KVAerospaceAnnotation()
      }

      if (json.inspectionArea) {
        annotation.tags = {
          ...annotation.tags,
          inspectionArea: {
            derivedTag: true,
            value: json.inspectionArea as string,
          },
        }
      }

      // Apply whiteboard tag immediately if in SystemView context
      if (kv.selectedWhiteboardId) {
        annotation.tags = {
          ...annotation.tags,
          whiteboard: {
            derivedTag: false,
            value: kv.selectedWhiteboardId,
          },
        }
      }

      // Update state with new annotation
      const updatedAnnotations = kv.annotations
        ? [...kv.annotations, annotation]
        : [annotation]
      kv.setAnnotations(updatedAnnotations)

      // Increment the annotation counter
      kv.setNextAnnotationNumber(kv.nextAnnotationNumber + 1)

      // Track annotation creation for session metrics
      recordAnnotationCreated()

      // Notify pixel stream about the label
      if (kv.pixelStream) {
        kv.pixelStream.emitUIInteraction({
          command: KestrelCommands.UPDATE_SURFACE_AREA,
          surfaceAreaId: annotation.id,
          surfaceAreaLabel: annotation.label,
        })
      }

      // Position popup in top right of screen
      const kvElementRect = kv.videoParent.current?.getBoundingClientRect()
      if (kvElementRect) {
        // Position in top right with some margin from edges
        const topRightX = kvElementRect.width - 475
        const topRightY = 20
        kv.setPopupMenuStartPosition(new Vector2(topRightX, topRightY))
      }
      kv.setSelectedMarkup(annotation)

      // Track annotation in rolodex using updated array to avoid race condition
      const patchData: RolodexEntryPatchJson = {
        id: kv.kestrelModel?.id ?? '',
        data: {
          annotations: updatedAnnotations.map((a) => KVAnnotation.toJSON(a)),
        },
      }
      kv.rolodexPatch([patchData])
    },
  } as KVMessageDef
}
